// Mapping of audit object types to their available field names for "Attribute Name" filtering
// Built from the audit log schema. Values are kept in snake_case to match backend attribute_name.

export const AUDIT_OBJECT_TYPE_TO_FIELDS: Record<string, readonly string[]> = {
  APICredentials: ['api_key', 'created_at', 'disabled', 'environment_id', 'id', 'is_cdp', 'name', 'site_id'],
  Account: [
    'account_manager_email',
    'account_manager_name',
    'account_manager_user_id',
    'account_state',
    'address_line1',
    'address_line2',
    'cancel_trial_at',
    'cancellation_reason',
    'cdp_overage_adjustment',
    'cdp_promo2023',
    'city',
    'company_website_url',
    'confirmation_code',
    'confirmed_at',
    'country',
    'created',
    'cs_access_granted_by',
    'cs_access_granted_on',
    'cs_access_granted_until',
    'csm_email',
    'csm_name',
    'csm_user_id',
    'custom_smtp_locked',
    'customer_overage_adjustment',
    'data_center',
    'delete_at',
    'delete_state',
    'disabled_delivery',
    'email',
    'enable_troubleshooting_cookies',
    'feature_flags',
    'gated_features',
    'hard_msg_count_limit',
    'hard_msg_count_limit_reached',
    'has_csm',
    'id',
    'invoice_email',
    'legal_email',
    'logo_url',
    'max_session_duration_hours',
    'name',
    'object_overage_adjustment',
    'ordway_id',
    'overages_plan',
    'overdue_date',
    'parent_id',
    'payment_failure_count',
    'pinned_coupon_code',
    'pinned_plan_id',
    'pinned_start_date',
    'premium_trial',
    'pricing_model',
    'product_activations',
    'rd_access_granted_by',
    'rd_access_granted_on',
    'reactivation_count',
    'read_only',
    'renewal_date',
    'setup_data',
    'setup_email',
    'shard',
    'show_feature_cv2',
    'soft_msg_count_limit',
    'soft_msg_count_limit_reached',
    'sso_setting',
    'state',
    'stop_compliance_check',
    'suspend_trial_at',
    'technical_email',
    'test_sends',
    'ts_access_granted_by',
    'ts_access_granted_on',
    'ts_access_granted_until',
    'two_factor_authentication_required',
    'unpaid_invoice_grace_days',
    'updated',
    'verification_state',
    'zip_code',
  ],
  Asset: [
    'created_at',
    'created_by',
    'deleted_at',
    'id',
    'in_use',
    'is_dir',
    'name',
    'parent_id',
    'path',
    'size',
    'updated_at',
    'updated_by',
  ],
  Audience: [
    'bitmap_id',
    'created_at',
    'has_consent',
    'id',
    'meta',
    'name',
    'segment_id',
    'state',
    'type',
    'updated_at',
  ],
  AudienceCredentials: ['created_at', 'data', 'expired', 'id', 'type', 'updated_at'],
  BillingSubscription: [
    'account_id',
    'cancellation_date',
    'contract_effective_date',
    'created',
    'created_date',
    'current_term_end_date',
    'end_date',
    'plan_id',
    'start_date',
    'status',
    'subscription_id',
    'updated',
    'usage_config',
  ],
  Campaign: ['actions', 'campaign'],
  CampaignAction: [
    'actions_in_branches',
    'add_to_seg',
    'allocations',
    'attribute_name',
    'attribute_value',
    'attributes_names',
    'attributes_values',
    'await_completion',
    'campaign_id',
    'can_convert',
    'cohorts',
    'conditional_filter',
    'created',
    'days_of_week',
    'debounce_secs',
    'default_action',
    'delay',
    'delay_event_attr',
    'delay_event_attr_offset',
    'enabled',
    'end_hour',
    'end_minutes',
    'env_id',
    'evaluation_version',
    'exit_type',
    'fsm',
    'holdout',
    'id',
    'last_updated',
    'message_limits_retry_window_secs',
    'multi_conditional_filter',
    'name',
    'next_action_ids',
    'optimal_time_days',
    'optimal_time_description',
    'optimal_time_end',
    'optimal_time_start',
    'override_global_message_limits_retry',
    'parent_id',
    'preconditions',
    'query_collection',
    'restart_min_interval',
    'restart_mode',
    'segment_id',
    'send_to_unsubs',
    'sending_state',
    'split_fsm',
    'split_started',
    'start_hour',
    'start_minutes',
    'sub_action_ids',
    'sub_type',
    'subscription_topic_ids',
    'template_id',
    'test_template_ids',
    'time',
    'tracked',
    'type',
    'use_customer_zone',
    'use_global_message_limits',
    'use_global_send_to_unsubs',
    'use_message_limits',
    'version',
    'warnings',
    'x',
    'y',
    'zone',
  ],
  Collection: [
    'created',
    'data_bytes',
    'description',
    'displayed_attributes',
    'id',
    'import_error',
    'import_type',
    'importing',
    'name',
    'rows',
    's3_import',
    'schema',
    'sheet_id',
    'spreadsheet_id',
    'updated',
    'url',
    'user_id',
  ],
  Condition: [
    'attribute_filter',
    'default_match',
    'event_filter',
    'event_type_id',
    'finished',
    'id',
    'processing',
    'related_object_attr_filter',
    'relationship_attr_filter',
    'relationship_filter',
    'segments_watching',
    'type',
  ],
  Credentials: ['action_type', 'config', 'created_at', 'encrypted_password', 'id', 'updated_at', 'username'],
  CustomForm: [
    'api_form_id',
    'attr_map_json',
    'attr_maps',
    'created_at',
    'error',
    'form_id',
    'form_type',
    'html_fingerprint',
    'html_form_id',
    'html_form_inputs',
    'html_form_name',
    'html_form_url',
    'id',
    'name',
    'status',
    'updated_at',
    'workspace_id',
  ],
  Domain: [
    'cname',
    'created',
    'dns_records',
    'domain',
    'encrypted_password',
    'fallback',
    'id',
    'ip_pool',
    'mailgun_domain',
    'mailgun_user',
    'updated',
    'use_http',
    'use_https',
    'verified',
    'verified_cname',
    'verified_dkim',
    'verified_dmarc',
    'verified_domain',
    'verified_hsts',
    'verified_spf',
    'verified_spf_manually',
  ],
  Export: [
    'created_at',
    'description',
    'done',
    'downloads',
    'failed',
    'id',
    'options',
    'path',
    'total',
    'type',
    'updated_at',
    'user_id',
  ],
  ExternalAPIKey: ['created_at', 'environment_id', 'hash', 'hint', 'id', 'name', 'updated_at'],
  Import: [
    'created',
    'description',
    'error',
    'filename',
    'id',
    'import_meta',
    'import_path',
    'import_rules',
    'name',
    'path',
    'pre_meta',
    'pre_rules',
    'preprocess_time',
    'rows_imported',
    'rows_to_import',
    'segment_id',
    'sheet_id',
    'source',
    'spreadsheet_id',
    'state',
    'type',
    'updated',
    'user_id',
    'validate_meta',
    'validate_rules',
    'validate_time',
  ],
  InAppBroadcast: [
    'audience',
    'config',
    'conversion',
    'created_at',
    'default_template_id',
    'description',
    'frequency',
    'id',
    'name',
    'state',
    'tag_ids',
    'templates',
    'updated_at',
  ],
  Integration: ['created_at', 'disabled', 'id', 'name', 'raw_errors', 'raw_settings', 'type', 'updated_at'],
  Layout: [
    'archived',
    'body',
    'created_at',
    'default',
    'emails_count',
    'id',
    'name',
    'newsletters_count',
    'starter_layout_id',
    'transactional_messages_count',
    'updated_at',
    'vars',
  ],
  Newsletter: [
    'attempted_count',
    'cancelled_at',
    'channel',
    'conversion_action',
    'conversion_event_filters',
    'conversion_event_name',
    'conversion_segment_id',
    'conversion_type',
    'conversion_window',
    'created',
    'deduped',
    'deleted',
    'egress_end_position',
    'has_conversion',
    'id',
    'is_paused',
    'last_delivery_created',
    'last_paused_at',
    'last_resumed_at',
    'message_count',
    'name',
    'optimal_date_time',
    'optimal_date_time_explanation',
    'optimal_date_time_selected',
    'original_recipient_count',
    'original_scheduled_at',
    'platform',
    'rate_limit_email_rate',
    'rate_limit_time_period',
    'recipient_list_set',
    'scheduled_at',
    'scheduled_send_to_remaining',
    'scheduled_timezone',
    'segment_groups',
    'send_percentage',
    'send_to_everyone',
    'send_to_unsubscribed',
    'sends_completed',
    'sent_at',
    'sent_to_remaining_at',
    'sto_enabled',
    'sto_recipient_list_set',
    'subscription_topic_ids',
    'tag_ids',
    'template_ids',
    'test_duration',
    'test_ended_at',
    'test_metric',
    'tracked',
    'tz_match_anchor_time',
    'tz_match_enabled',
    'updated',
    'use_message_limits',
    'use_new_channel_for_push',
    'winner_template_id',
  ],
  ObjectType: [
    'color',
    'enabled',
    'has_non_ui_objects',
    'icon',
    'id',
    'name',
    'singular_name',
    'singular_slug',
    'slug',
    'source',
    'state',
  ],
  Plan: [
    'active',
    'addon',
    'annual_slug',
    'auto_upgrade',
    'cancellable',
    'cdp_premium_price',
    'created',
    'gated_features',
    'high_watermark',
    'id',
    'interval',
    'interval_count',
    'metered_customer_rate',
    'metering_type',
    'monthly_cdp_calls',
    'monthly_customers',
    'monthly_messages',
    'monthly_objects',
    'name',
    'objects_overage_rate',
    'plan_type',
    'planned_custom_smtp',
    'premium',
    'premium_description',
    'price',
    'slug',
    'updated',
  ],
  ProfileDeletion: ['filters', 'internal_ids', 'type'],
  ProfileMerge: ['additional_data', 'env_id', 'internal1', 'internal2', 'source'],
  ProfileShortcut: [
    'body',
    'created_at',
    'encrypted_headers',
    'encrypted_url',
    'id',
    'is_webhook',
    'name',
    'request_method',
    'updated_at',
  ],
  Receipt: [
    'account_id',
    'created',
    'id',
    'invoice_id',
    'invoiced',
    'metered_cdp_calls',
    'metered_messages',
    'metered_objects',
    'metered_profiles',
    'overage_cdp_calls_count',
    'overage_message_count',
    'overage_objects_count',
    'overage_profile_count',
    'paid_at',
    'period_end',
    'period_start',
    'plan_interval',
    'plan_name',
    'plan_price',
    'plan_slug',
    'raw_json',
    'status',
    'subtotal',
    'total',
    'type',
    'updated',
  ],
  Role: ['account_id', 'created', 'created_by', 'description', 'id', 'name', 'role_type', 'updated', 'updated_by'],
  SCIMConfiguration: ['account_id', 'created', 'default_role_id', 'default_workspace_id', 'tokens', 'updated'],
  SCIMToken: ['account_id', 'created', 'id', 'token', 'updated'],
  SSOConfiguration: [
    'account_id',
    'created',
    'identity_provider',
    'oidc_client_id',
    'oidc_encrypted_client_secret',
    'oidc_google_hosted_domain',
    'oidc_issuer_url',
    'saml_encryption_cert',
    'saml_encryption_key',
    'saml_idp_metadata_url',
    'saml_metadata_id',
    'updated',
  ],
  SavedRow: ['category_id', 'created_at', 'data', 'id', 'name', 'updated_at'],
  Segment: [
    'archived',
    'auto_archived_at',
    'backfill_id',
    'conditions',
    'created',
    'description',
    'id',
    'last_used_timestamp',
    'linked_form_id',
    'linked_form_type',
    'name',
    'processing',
    'sample',
    'segments_watching',
    'tag_ids',
    'type',
    'updated',
    'version',
  ],
  SenderIdentity: ['auto_generated', 'created_at', 'email', 'id', 'name', 'phone', 'template_type', 'updated_at'],
  Session: [
    'account_id',
    'activated',
    'created',
    'current_sign_in_at',
    'current_sign_in_ip',
    'deleted',
    'email',
    'email2_fa_enabled',
    'email_changed_at',
    'email_changed_count',
    'external_identity_email',
    'external_identity_provider',
    'external_identity_subject',
    'first_name',
    'google_analytics_client_id',
    'google_drive_credential',
    'id',
    'last_environment_id',
    'last_name',
    'last_sign_in_at',
    'last_sign_in_ip',
    'login_method',
    'otp_secret_key',
    'password',
    'reset_password_requested_by',
    'reset_password_sent_at',
    'reset_password_token',
    'role',
    'subscriptions',
    'updated',
    'user_flags',
  ],
  Snippet: ['id', 'name', 'updated_at', 'value', 'vars'],
  Subscription: [
    'account_id',
    'active',
    'cancelled',
    'closed',
    'coupon_code',
    'coupon_end',
    'created',
    'id',
    'period_end',
    'period_start',
    'plan_id',
    'start_date',
    'status',
    'stripe_customer_id',
    'stripe_overages_id',
    'stripe_subscription_id',
    'updated',
  ],
  SubscriptionCenterSettings: ['background_color', 'foreground_color', 'logo_path', 'logo_source'],
  SubscriptionTopic: ['created_at', 'description', 'id', 'name', 'opt_in', 'updated_at'],
  Tag: ['color', 'created_at', 'id', 'name', 'updated_at'],
  TaxRate: ['account_id', 'combined_rate', 'created', 'id', 'raw_data'],
  Template: [
    'action_id',
    'bcc',
    'body',
    'body_amp',
    'body_json',
    'body_plain',
    'body_processed',
    'created_at',
    'created_by',
    'deleted',
    'editor',
    'email_layout_id',
    'encrypted_headers',
    'encrypted_url',
    'fake_bcc',
    'from',
    'from_address',
    'from_name',
    'id',
    'image_url',
    'in_app_broadcast_id',
    'language',
    'layout',
    'layout_vars',
    'name',
    'newsletter_id',
    'parent_action_id',
    'preheader_text',
    'preprocessor',
    'recipient',
    'recipient_environment_id',
    'reply_to',
    'reply_to_identity',
    'request_method',
    'sender_identity_id',
    'template_engine',
    'template_type',
    'test_group_id',
    'title',
    'transactional_message_id',
    'updated_at',
    'updated_by',
    'vars',
    'weight',
    'whatsapp',
  ],
  TransactionalMessage: [
    'content_source',
    'created',
    'default',
    'deleted',
    'description',
    'hide_message_body',
    'id',
    'link_tracking',
    'name',
    'open_tracking',
    'queue_drafts',
    'sample_data',
    'send_to_unsubscribed',
    'state',
    'tag_ids',
    'template_id',
    'trigger_name',
    'type',
    'updated',
  ],
  TwilioCredentials: ['active', 'auth_token_encrypted', 'sid', 'workspace_id'],
  User: [
    'account_id',
    'account_permissions',
    'activated',
    'created',
    'current_sign_in_at',
    'current_sign_in_ip',
    'deleted',
    'email',
    'email2_fa_enabled',
    'email_changed_at',
    'email_changed_count',
    'env_roles',
    'external_identity_email',
    'external_identity_provider',
    'external_identity_subject',
    'first_name',
    'google_drive_credential',
    'id',
    'last_environment_id',
    'last_name',
    'last_sign_in_at',
    'last_sign_in_ip',
    'otp_secret_key',
    'password',
    'reset_password_requested_by',
    'reset_password_sent_at',
    'reset_password_token',
    'role',
    'subscriptions',
    'updated',
    'user_flags',
  ],
  WhitelistedIP: ['created_at', 'environment_id', 'id', 'ip'],
  Workspace: [
    'account_id',
    'billed',
    'client_settings',
    'created',
    'default_preprocess_css',
    'default_template_engine',
    'deleted_at',
    'delivery_email',
    'delivery_settings',
    'disabled',
    'disabled_channels',
    'edge_forwarding',
    'edge_limits',
    'email_open_tracking',
    'external_id',
    'hex_code',
    'human_metrics_only',
    'id',
    'identifier_type',
    'identifiers_config',
    'initialized',
    'language_attribute',
    'metric_denominator',
    'migrated',
    'migrating',
    'name',
    'objects_billing',
    'secret',
    'shard',
    'signing_secret',
    'smart_encode_sms',
    'stop_processing',
    'subscription_center_enabled',
    'updated',
    'workspace_id',
  ],
  WorkspacePermissions: ['access_to_all', 'environment_id', 'roles', 'user_id', 'user_ids'],
};
